import React, { useState } from "react";
import { router } from "@inertiajs/react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import DashboardLayout from "../../components/DashboardLayout";
import {
  Save,
  Settings,
  Smartphone,
  Mail,
  MapPin,
  Facebook,
  Instagram,
  Upload,
  Image,
  X,
  Trash2,
} from "lucide-react";
import { FaTiktok } from "react-icons/fa";

import { SortableMultiSelect } from "@/components/ui/sortable-multi-select";

interface User {
  email: string;
}

interface Category {
  id: number;
  name: string;
}

interface StoreSetting {
  id: number;
  key: string;
  value: string;
  description: string;
  logo_url?: string;
}

interface PageProps {
  auth: {
    user: User;
  };
  store_settings: StoreSetting[];
  categories: Category[];
  store_logo?: string;
  store_name?: string;
}

const StoreSettings = ({
  store_settings: initialSettings,
  categories,
  store_logo,
  store_name,
}: Omit<PageProps, 'auth'>) => {
  const [settings, setSettings] = useState(initialSettings || []);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const homeCategoryIdsSetting = settings.find(
    (s) => s.key === "home_category_ids",
  );
  const initialHomeCategoryIds = homeCategoryIdsSetting
    ? JSON.parse(homeCategoryIdsSetting.value)
    : [];
  const [homeCategories, setHomeCategories] = useState<
    { value: string; label: string }[]
  >(
    initialHomeCategoryIds
      .map((id: any) => categories.find((c) => c.id === id))
      .filter(Boolean)
      .map((c: any) => ({ value: c.id.toString(), label: c.name })),
  );

  const handleSettingChange = (id: number, field: string, value: string) => {
    setSettings((prev) =>
      prev.map((setting) =>
        setting.id === id ? { ...setting, [field]: value } : setting,
      ),
    );
  };

  const handleSave = () => {
    const homeCategoryIds = homeCategories.map((c) => parseInt(c.value, 10));
    console.log("Home categories to save:", homeCategoryIds);
    const settingsWithHomeCategories = settings.map((s) => {
      if (s.key === "home_category_ids") {
        return { ...s, value: JSON.stringify(homeCategoryIds) };
      }
      return s;
    });

    console.log("Settings to send:", settingsWithHomeCategories.map((s) => ({ id: s.id, value: s.value })));

    router.patch(
      "/admin/store_settings/update_all",
      {
        settings: settingsWithHomeCategories.map((s) => ({
          id: s.id,
          value: s.value,
        })),
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setSaveMessage("Configuraciones guardadas exitosamente.");
          setTimeout(() => setSaveMessage(null), 3000);
        },
        onError: (errors) => {
          console.error("Failed to update settings:", errors);
          setSaveMessage("Error al guardar las configuraciones.");
          setTimeout(() => setSaveMessage(null), 3000);
        },
      },
    );
  };

  const handleDeleteSetting = (id: number) => {
    if (confirm("¿Estás seguro de que quieres eliminar esta configuración?")) {
      router.delete(`/admin/store_settings/${id}`, {
        preserveScroll: true,
        onSuccess: () => {
          setSettings((prev) => prev.filter((setting) => setting.id !== id));
        },
      });
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
    }
  };

  const handleSaveLogo = () => {
    if (!logoFile) return;

    router.post(
      "/admin/store_settings/update_logo",
      {
        logo: logoFile,
      },
      {
        onSuccess: () => {
          setIsLogoModalOpen(false);
          setLogoFile(null);
        },
        onError: (errors) => {
          console.error("Error uploading logo:", errors);
        },
      },
    );
  };

  const handleRemoveLogo = () => {
    if (confirm("¿Estás seguro de que quieres eliminar el logo?")) {
      router.delete("/admin/store_settings/remove_logo", {
        preserveScroll: true,
        onSuccess: () => {
          window.location.reload();
        },
        onError: (errors) => {
          console.error("Error removing logo:", errors);
        },
      });
    }
  };

  const getStoreLogo = () => {
    return settings.find((s) => s.key === "store_logo")?.logo_url;
  };

  const getIconForKey = (key: string) => {
    switch (key) {
      case "store_name":
      case "store_slogan":
      case "store_logo":
        return <Settings className="h-4 w-4" />;
      case "whatsapp_number":
      case "contact_phone":
        return <Smartphone className="h-4 w-4" />;
      case "contact_email":
        return <Mail className="h-4 w-4" />;
      case "address":
        return <MapPin className="h-4 w-4" />;
      case "products_pdf_url":
        return <Upload className="h-4 w-4" />;
      case "social_facebook":
        return <Facebook className="h-4 w-4" />;
      case "social_instagram":
        return <Instagram className="h-4 w-4" />;
      case "social_tiktok":
        return <FaTiktok className="h-4 w-4" />;
      default:
        return <Settings className="h-4 w-4" />;
    }
  };

  return (
    <DashboardLayout
      title="Configuración de Tienda"
      storeLogo={store_logo}
      storeName={store_name}
    >
      <div className="relative mb-8 p-8 rounded-2xl bg-gradient-to-r from-slate-400 via-slate-500 to-slate-600 shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-300/20 via-slate-400/20 to-slate-500/20 animate-pulse" />
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-xl" />
        <div className="relative z-10 flex items-center justify-between">
           <div>
             <h1 className="text-4xl font-bold text-white mb-2">
               Configuración de Tienda
             </h1>
             <p className="text-slate-50 text-lg">
               Gestiona la información y apariencia de tu tienda
             </p>
             {saveMessage && (
               <p className={`mt-2 text-sm ${saveMessage.includes('Error') ? 'text-red-300' : 'text-green-300'}`}>
                 {saveMessage}
               </p>
             )}
           </div>
           <Button onClick={handleSave} className="bg-white text-slate-700 hover:bg-slate-50 shadow-lg hover:shadow-xl transition-all duration-200">
             <Save className="h-5 w-5 mr-2" />
             Guardar Cambios
           </Button>
         </div>
      </div>

      <Card className="mb-8 border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="p-2 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-600 shadow-lg">
              <Image className="h-6 w-6 text-white" />
            </div>
            <span>Logo de la Tienda</span>
          </CardTitle>
          <CardDescription className="text-base text-slate-600 dark:text-slate-400">
            Configura el logo que aparecerá en el dashboard y navegación
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-6">
            <div className="flex-shrink-0">
              {getStoreLogo() ? (
                <div className="relative">
                  <img
                    src={getStoreLogo()}
                    alt="Logo de la tienda"
                    className="h-24 w-24 object-contain border-2 border-slate-200 dark:border-slate-700 rounded-xl shadow-sm"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute -top-2 -right-2 h-7 w-7 rounded-full p-0 shadow-lg hover:shadow-xl transition-shadow"
                    onClick={handleRemoveLogo}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="h-24 w-24 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl flex items-center justify-center bg-slate-50 dark:bg-slate-800">
                  <Image className="h-10 w-10 text-slate-400 dark:text-slate-500" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                {getStoreLogo()
                  ? 'Logo actual de la tienda. Haz clic en "Cambiar Logo" para actualizarlo.'
                  : 'No hay logo configurado. Haz clic en "Subir Logo" para agregar uno.'}
              </p>
              <Button
                onClick={() => setIsLogoModalOpen(true)}
                variant="outline"
                className="hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-900/20 dark:hover:to-indigo-900/20 border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-300 hover:border-purple-400 dark:hover:border-purple-500 transition-all duration-200"
              >
                <Upload className="h-4 w-4 mr-2" />
                {getStoreLogo() ? "Cambiar Logo" : "Subir Logo"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Información Básica</span>
          </CardTitle>
          <CardDescription>
            Configura la información principal de tu tienda
          </CardDescription>
        </CardHeader>
         <CardContent className="space-y-6">
            {settings
              .filter((s) => ["store_name", "store_slogan", "about_text", "products_pdf_url"].includes(s.key))
             .map((setting) => (
              <div key={setting.id} className="space-y-2">
                <div className="flex items-center space-x-2">
                  {getIconForKey(setting.key)}
                  <Label htmlFor={setting.key} className="text-sm font-medium">
                    {setting.description}
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteSetting(setting.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {setting.key === "about_text" ? (
                  <Textarea
                    id={setting.key}
                    value={setting.value}
                    onChange={(e) =>
                      handleSettingChange(setting.id, "value", e.target.value)
                    }
                    placeholder={setting.description}
                    rows={3}
                  />
                ) : (
                  <Input
                    id={setting.key}
                    value={setting.value}
                    onChange={(e) =>
                      handleSettingChange(setting.id, "value", e.target.value)
                    }
                    placeholder={setting.description}
                  />
                )}
              </div>
            ))}
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Smartphone className="h-5 w-5" />
            <span>Información de Contacto</span>
          </CardTitle>
          <CardDescription>
            Configura cómo los clientes pueden contactarte
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {settings
            .filter((s) =>
              [
                "whatsapp_number",
                "contact_email",
                "contact_phone",
                "address",
              ].includes(s.key),
            )
            .map((setting) => (
              <div key={setting.id} className="space-y-2">
                <div className="flex items-center space-x-2">
                  {getIconForKey(setting.key)}
                  <Label htmlFor={setting.key} className="text-sm font-medium">
                    {setting.description}
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteSetting(setting.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Input
                  id={setting.key}
                  value={setting.value}
                  onChange={(e) =>
                    handleSettingChange(setting.id, "value", e.target.value)
                  }
                  placeholder={setting.description}
                />
              </div>
            ))}
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Facebook className="h-5 w-5" />
            <span>Redes Sociales</span>
          </CardTitle>
          <CardDescription>
            Enlaces a tus perfiles en redes sociales
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {settings
            .filter((s) => s.key.startsWith("social_"))
            .map((setting) => (
              <div key={setting.id} className="space-y-2">
                <div className="flex items-center space-x-2">
                  {getIconForKey(setting.key)}
                  <Label htmlFor={setting.key} className="text-sm font-medium">
                    {setting.description}
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteSetting(setting.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                 <Input
                   id={setting.key}
                   value={setting.value}
                   onChange={(e) =>
                     handleSettingChange(setting.id, "value", e.target.value)
                   }
                   placeholder={setting.key === "products_pdf_url" ? "https://ejemplo.com/catalogo.pdf" : `URL de ${setting.description}`}
                 />
              </div>
            ))}
        </CardContent>
      </Card>

       <Card className="mb-8">
         <CardHeader>
           <CardTitle>Categorías en Home</CardTitle>
           <CardDescription>
             Selecciona las categorías que aparecerán en la página de inicio y ordena su aparición.
           </CardDescription>
         </CardHeader>
         <CardContent>
            <SortableMultiSelect
              frameworks={categories.map((c) => ({
                value: c.id.toString(),
                label: c.name,
              }))}
              selected={homeCategories}
              setSelected={setHomeCategories}
              placeholder="Seleccionar categorías..."
            />
         </CardContent>
       </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Políticas y Términos</CardTitle>
          <CardDescription>
            Información sobre envíos, devoluciones y políticas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {settings
            .filter((s) => ["shipping_info", "return_policy"].includes(s.key))
            .map((setting) => (
              <div key={setting.id} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Label htmlFor={setting.key} className="text-sm font-medium">
                    {setting.description}
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteSetting(setting.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Textarea
                  id={setting.key}
                  value={setting.value}
                  onChange={(e) =>
                    handleSettingChange(setting.id, "value", e.target.value)
                  }
                  placeholder={setting.description}
                  rows={2}
                />
              </div>
            ))}
        </CardContent>
      </Card>

      <Dialog open={isLogoModalOpen} onOpenChange={setIsLogoModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar Logo de la Tienda</DialogTitle>
            <DialogDescription>
              Sube una imagen para usar como logo de tu tienda. Se recomienda
              usar imágenes PNG o SVG con fondo transparente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="logo-upload">Seleccionar Imagen</Label>
              <Input
                id="logo-upload"
                type="file"
                accept="image/png, image/svg+xml"
                onChange={handleLogoUpload}
                className="cursor-pointer"
              />
            </div>

            {logoFile && (
              <div className="space-y-2">
                <Label>Vista Previa</Label>
                <div className="border rounded-lg p-4 flex justify-center">
                  <img
                    src={URL.createObjectURL(logoFile)}
                    alt="Preview del logo"
                    className="max-h-32 max-w-32 object-contain"
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Archivo seleccionado: {logoFile.name}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLogoModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveLogo}
              disabled={!logoFile}
            >
              <Upload className="h-4 w-4 mr-2" />
              Subir Logo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default StoreSettings;

